import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Spin,
  message,
  Divider,
  Space,
  Row,
  Col,
  Typography,
  Card,
  Empty,
  Tooltip,
  Switch,
} from 'antd';
import {
  getPromptAction,
  listLlms,
  listPromptsAction,
  listPromptScreens,
  updatePromptAction,
} from '../../../../../store/actions/promptsv2.action';
import { getSRTypeList } from 'src/modules/SuperAdmin/store/actions/service_request';
import { listAgentsAction } from 'src/modules/SuperAdmin/store/actions/agents.action';
import {
  getLlmDisplayText,
  getLlmDisplayComponent,
  shouldShowTopP,
} from 'src/utils/llmUtils';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import KeyValuePairEditor from './KeyValuePairEditor';
import OrganizationFilter from '../../OrganizationFilter';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const EditPromptModal = ({
  visible,
  setVisible,
  promptId,
  filters,
  selectedOrganizationUUID,
  scope,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isKeyValueMode, setIsKeyValueMode] = useState(false);

  // Watch all form fields
  const watchedFormValues = Form.useWatch([], form);
  const watchedLlmId = Form.useWatch('llm_id', form);

  const {
    prompt,
    promptLoading,
    llms,
    llmsLoading,
    promptScreens,
    promptScreensLoading,
  } = useSelector((state) => state.promptsState);
  const {
    conditions,
    conditionsLoading,
    specialties,
    serviceRequestTypes,
    serviceRequestTypesLoading,
    agentsList,
    agentsLoading,
  } = useSelector((state) => ({
    conditions: state?.conditionState?.conditionsDropdown,
    conditionsLoading: state?.conditionState?.conditionsLoading,
    specialties: state?.specialtyState?.getAllSpecialtiesList,
    serviceRequestTypes: state?.serviceRequestState?.srTypeList,
    serviceRequestTypesLoading: state?.serviceRequestState?.srTypeListLoading,
    agentsList: state?.agentsState?.agentsList,
    agentsLoading: state?.agentsState?.agentsLoading,
  }));

  useEffect(() => {
    if (visible && promptId) {
      dispatch(getPromptAction(promptId));
      dispatch(listLlms());
      dispatch(listPromptScreens(null, null, scope));
      if (scope === 'service_request') {
        dispatch(getSRTypeList(selectedOrganizationUUID));
      }
      if (scope === 'agent') {
        dispatch(listAgentsAction(selectedOrganizationUUID));
      }
    } else {
      form.resetFields();
    }
  }, [dispatch, visible, promptId, form, selectedOrganizationUUID, scope]);

  useEffect(() => {
    if (prompt) {
      form.setFieldsValue({
        ...prompt,
      });

      // Detect if prompt content is JSON
      try {
        JSON.parse(prompt.prompt);
        setIsKeyValueMode(true);
      } catch (e) {
        setIsKeyValueMode(false);
      }
    }
  }, [prompt, form]);

  const onClose = () => {
    form.resetFields();
    setIsSubmitting(false);
    setVisible(false);
  };

  const handleFinish = async (values) => {
    setIsSubmitting(true);
    const payload = { ...values, scope };

    // Check if selected LLM has reasoning_effort and set temperature to 1
    const selectedLlm = llms?.llms?.find((llm) => llm.llm_id === values.llm_id);
    if (selectedLlm?.reasoning_effort) {
      payload.temp = 1;
    }

    // Unset top_p for models that don't support it (reasoning models, Anthropic, etc.)
    if (!shouldShowTopP(selectedLlm)) {
      payload.top_p = null;
    }

    // Ensure the prompt is properly formatted
    if (isKeyValueMode && typeof payload.prompt === 'string') {
      try {
        // Validate that it's proper JSON
        JSON.parse(payload.prompt);
      } catch (e) {
        message.error('Invalid JSON in prompt field');
        setIsSubmitting(false);
        return;
      }
    }

    dispatch(
      updatePromptAction(promptId, payload, () => {
        setIsSubmitting(false);
        onClose();
        dispatch(
          listPromptsAction({ ...filters, scope }, selectedOrganizationUUID)
        );
      })
    );
  };

  const toggleEditorMode = (checked) => {
    setIsKeyValueMode(checked);
  };

  // Check if selected LLM has reasoning_effort
  const selectedLlm = llms?.llms?.find((llm) => llm.llm_id === watchedLlmId);
  const hasReasoningEffort = selectedLlm?.reasoning_effort;
  const showTopP = shouldShowTopP(selectedLlm);

  // Determine if OrganizationFilter should be disabled based on scope
  const isOrganizationFilterDisabled = () => {
    // Global scope (organization filter should be disabled for specialty scope)
    return scope === 'specialty' || scope === 'magicflow';
  };

  return (
    <Modal
      title={
        <div>
          <Text>Edit Prompt</Text>
          <div style={{ marginTop: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Version: {prompt?.version}
            </Text>
            <Text
              type="secondary"
              style={{ fontSize: '12px', marginLeft: '8px' }}
            >
              Prompt ID: {promptId}
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      maskClosable={false}
    >
      <Spin
        spinning={
          promptLoading ||
          llmsLoading ||
          promptScreensLoading ||
          conditionsLoading ||
          agentsLoading ||
          isSubmitting
        }
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          onFinish={handleFinish}
          initialValues={prompt}
        >
          {/* Organization Filter Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              Organization Scope
            </h3>
            <Form.Item label="Organization">
              <OrganizationFilter disabled={isOrganizationFilterDisabled()} />
            </Form.Item>
          </div>

          {/* Prompt Details Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              Prompt Details
            </h3>
            <Form.Item name="description" label="Description">
              <TextArea rows={1} placeholder="Enter description" />
            </Form.Item>
            <Form.Item
              name="prompt_screens_id"
              label="Prompt Screen"
              rules={[
                {
                  required: true,
                  message: 'Please select a prompt screen!',
                },
              ]}
            >
              <Select
                placeholder="Select a screen"
                loading={promptScreensLoading}
                showSearch
                optionLabelProp="label"
                filterOption={(input, option) =>
                  (option.label || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {promptScreens?.prompt_screens?.map((screen) => (
                  <Select.Option
                    key={screen.prompt_screens_id}
                    value={screen.prompt_screens_id}
                    label={screen.prompt_screen}
                  >
                    <div className="flex flex-col">
                      <div className="font-medium">{screen.prompt_screen}</div>
                      {screen.description && (
                        <div className="text-xs text-gray-500">
                          {screen.description}
                        </div>
                      )}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {scope === 'specialty' && (
              <Form.Item
                name="condition_id"
                label="Condition"
                rules={[{ required: false }]}
              >
                <Select
                  placeholder="Select a condition"
                  loading={conditionsLoading}
                  allowClear
                  showSearch
                  options={conditions?.map((condition) => ({
                    label: condition.condition_name,
                    value: condition.condition_id,
                  }))}
                />
              </Form.Item>
            )}

            {scope === 'service_request' && (
              <Form.Item
                name="service_request_type_id"
                label="Service Request Type"
                rules={[{ required: false }]}
              >
                <Select
                  placeholder="Select a service request type"
                  loading={serviceRequestTypesLoading}
                  allowClear
                  showSearch
                  options={serviceRequestTypes?.map((serviceRequestType) => ({
                    label: serviceRequestType.service_request_type,
                    value: serviceRequestType.service_request_type_id,
                  }))}
                />
              </Form.Item>
            )}

            {scope === 'agent' && (
              <Form.Item
                name="agent_id"
                label="Agent"
                rules={[{ required: false }]}
              >
                <Select
                  placeholder="Select an agent"
                  loading={agentsLoading}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  options={agentsList?.agents?.map((agent) => ({
                    label: agent.name,
                    value: agent.agent_id,
                  }))}
                />
              </Form.Item>
            )}

            <Form.Item
              name="prompt"
              label="Prompt Template"
              rules={[
                {
                  required: true,
                  message: 'Please enter the prompt template!',
                },
              ]}
              className="w-full"
            >
              <div className="flex justify-end p-4">
                <Space>
                  <Text>Text</Text>
                  <Switch
                    checked={isKeyValueMode}
                    onChange={toggleEditorMode}
                  />
                  <Text>Sections</Text>
                </Space>
              </div>
              {isKeyValueMode ? (
                <KeyValuePairEditor
                  value={watchedFormValues?.prompt}
                  onChange={(value) => form.setFieldsValue({ prompt: value })}
                />
              ) : (
                <TextArea
                  rows={12}
                  placeholder="Enter prompt template"
                  value={watchedFormValues?.prompt}
                  onChange={(e) =>
                    form.setFieldsValue({ prompt: e.target.value })
                  }
                />
              )}
            </Form.Item>
            <Form.Item name="num_questions" label="Number of Questions">
              <InputNumber
                placeholder="Enter number of questions"
                className="w-full"
                min={1}
                step={1}
              />
            </Form.Item>
          </div>

          {/* LLM Settings Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
              LLM Settings
            </h3>
            <Form.Item
              name="llm_id"
              label="LLM"
              rules={[{ required: true, message: 'Please select a LLM!' }]}
            >
              <Select
                placeholder="Select a LLM"
                loading={llmsLoading}
                showSearch
                optionLabelProp="label"
                onChange={(value) => {
                  form.setFieldsValue({ llm_id: value });

                  // Find the selected LLM and clear top_p if not supported
                  const selectedLlm = llms?.llms?.find(
                    (llm) => llm.llm_id === value
                  );
                  if (selectedLlm && !shouldShowTopP(selectedLlm)) {
                    form.setFieldsValue({ top_p: undefined });
                  }
                }}
              >
                {llms?.llms?.map((llm) => {
                  const displayText = getLlmDisplayText(llm);

                  return (
                    <Select.Option
                      key={llm.llm_id}
                      value={llm.llm_id}
                      label={displayText}
                    >
                      <div className="flex flex-col">
                        <div className="font-medium">
                          {getLlmDisplayComponent(llm)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {llm.provider || llm.description || llm.model_type}
                        </div>
                      </div>
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            {!hasReasoningEffort && (
              <Form.Item
                name="temp"
                label="Temperature"
                rules={[
                  { required: true, message: 'Please select a temperature!' },
                ]}
              >
                <InputNumber
                  placeholder="Enter temperature"
                  value={prompt.temperature}
                  onChange={(value) => form.setFieldsValue({ temp: value })}
                  className="w-full"
                />
              </Form.Item>
            )}
            <Form.Item
              name="max_token"
              label="Max Tokens"
              rules={[
                { required: true, message: 'Please select a max tokens!' },
              ]}
            >
              <InputNumber
                placeholder="Enter max tokens"
                value={prompt.max_token}
                onChange={(value) => form.setFieldsValue({ max_token: value })}
                className="w-full"
              />
            </Form.Item>
            {showTopP && (
              <Form.Item name="top_p" label="Top P">
                <InputNumber
                  placeholder="Enter top p"
                  value={prompt.top_p}
                  onChange={(value) => form.setFieldsValue({ top_p: value })}
                  className="w-full"
                />
              </Form.Item>
            )}
            <Form.Item
              name="tools"
              label="Tools"
              rules={[
                {
                  required: false,
                  message: 'Please enter the tools JSON!',
                },
              ]}
            >
              <TextArea
                placeholder='Enter tools configuration as JSON (e.g., ["tool1", "tool2"])'
                className="w-full"
              />
            </Form.Item>
          </div>
          <Divider />
          <Form.Item wrapperCol={{ offset: 0, span: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" htmlType="submit" loading={isSubmitting}>
                Update
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditPromptModal;
